import { reaction } from 'mobx';
import {
  actions,
  gameGlobalStates,
  PowerOfGodsSoundsNames,
  winTypesEnum,
} from '../powerOfGodsCore/constants/globalConstants';
import { freeBetStore } from '../../../store/freeBetStore';
import { checkHashStore } from '../../../store/checkHashStore';
import { ValidationType } from '../../../core/constants/validationType';
import { userInfoStore } from '../../../store/userInfoStore';
import { notificationStore } from '../../../store/notificationStore';
import { mainGameConnection } from '../../../core/mainGameConnection/mainGameConnection';
import { powerOfGodsStore } from '../powerOfGodsStores/powerOfGodsStore';
import { lineNamesInBackEnd, RINGS } from '../powerOfGodsCore/constants/matrixConstants';
import { betHistoryStore } from '../../../store/betHistoryStore';
import { powerOfGodsGameConnection } from '../powerOfGodsConnection';
import { worker } from '../../../index';
import { noWinSymbol } from '../powerOfGodsCore/constants/reelConstants';
import { powerOfGodsBetsStore } from '../powerOfGodsStores/betStore';
import { soundService } from '../../../services/baseSoundService/soundService';
import { gameController } from '../powerOfGodControllers/gameController';
import { TranslationKeys } from '../../../core/constants/translationKeys';
import { applicationStore } from '../../../store/applicationStore';
import { powerOfGodsHotKeysStore } from '../powerOfGodsStores/hotKeysStore';

class PowerOfGodsGameService {
  onGetInitialStateResult(payload) {
    const {
      additionalInfo,
      lastSpinInfo,
      userFreebets,
      quickBets,
      responseCode,
      defaultBetAmount,
    } = payload;
    applicationStore.setIsGameCustomView(true);
    powerOfGodsHotKeysStore.setIsStartHotKeys(true);
    if (responseCode === ValidationType.Success) {
      userInfoStore.setLimits({
        minBet: quickBets[0],
        maxBet: quickBets[quickBets.length - 1],
      });
      powerOfGodsStore.setQuickBets(quickBets);
      if (lastSpinInfo) {
        const { salt, key, hash } = lastSpinInfo;
        if (salt && key && hash) {
          checkHashStore.setLastRoundInfo({ salt, hash, key });
        }
        powerOfGodsStore.setWinAmount(lastSpinInfo.winInfo?.winAmount);
        checkHashStore.setLastRoundResult({
          id: lastSpinInfo.id,
          time: lastSpinInfo.date,
          resultSymbol: lastSpinInfo.resultSymbol,
        });
      }
      const { oddsInfo } = additionalInfo;
      const { line1AutoCashout, line2AutoCashout } = oddsInfo;
      const { currentState } = additionalInfo;
      let ringStates = new Array(RINGS.length);

      if (currentState.status === 0 || currentState.status === 1) {
        const {
          line1,
          line2,
          line3,
          possibleActions = [],
          bonusInfo,
          resultSymbol,
          cashoutWinAmount,
          partCashoutWinAmount,
        } = currentState;
        powerOfGodsStore.setCashoutWinAmount(cashoutWinAmount);
        powerOfGodsStore.setPartCashoutWinAmount(partCashoutWinAmount);
        RINGS.forEach((ring, index) => {
          ringStates[index] = 0;
        });
        if (currentState.status) {
          powerOfGodsBetsStore.setIsAllowToChangeBetAmount(false);
          notificationStore.setNotification(ValidationType.GameReturnedNotify);
        }
        if (lastSpinInfo !== null) {
          userInfoStore.setBetAmount(lastSpinInfo.amount || defaultBetAmount);
          powerOfGodsHotKeysStore.setIsStartHotKeys(true);
          powerOfGodsStore.setInitialStateSymbol(lastSpinInfo.resultSymbol);
        } else {
          powerOfGodsStore.setInitialStateSymbol(resultSymbol);
        }
        powerOfGodsStore.setIsAllowBet(possibleActions.includes(actions.bet));
        powerOfGodsStore.setIsAllowCashOut(possibleActions.includes(actions.cashOut));
        powerOfGodsStore.setIsAllowPartCashPut(possibleActions.includes(actions.partCashOut));
        ringStates = [line3, line2, line1];
        powerOfGodsStore.setInitialRingStates(ringStates);
        powerOfGodsStore.setInitialBonusInfo(bonusInfo);
      } else {
        notificationStore.setNotification(ValidationType.GameReturnedNotify);
        const {
          line1,
          line2,
          line3,
          resultSymbol,
          possibleActions = [],
          cashoutWinAmount,
          partCashoutWinAmount,
          bonusInfo,
        } = currentState;
        powerOfGodsStore.setCashoutWinAmount(cashoutWinAmount);
        powerOfGodsStore.setPartCashoutWinAmount(partCashoutWinAmount);
        powerOfGodsStore.setIsAllowBet(possibleActions.includes(actions.bet));
        powerOfGodsStore.setIsAllowCashOut(possibleActions.includes(actions.cashOut));
        powerOfGodsStore.setIsAllowPartCashPut(possibleActions.includes(actions.partCashOut));
        powerOfGodsBetsStore.setIsAllowToChangeBetAmount(!line1 && !line2 && !line3);
        ringStates = [line3, line2, line1];
        powerOfGodsStore.setInitialStateSymbol(resultSymbol);
        powerOfGodsStore.setInitialRingStates(ringStates);
        powerOfGodsStore.setInitialBonusInfo(bonusInfo);
      }
      freeBetStore.setFreeBetList(userFreebets || []);
      // powerOfGodsHotKeysStore.setIsStartHotKeys(false);

      RINGS.forEach((ringName, index) => {
        powerOfGodsStore.setOddsInfo(ringName, oddsInfo[lineNamesInBackEnd[index]]);
      });

      powerOfGodsStore.setLine1AutoCashOut(line1AutoCashout);
      powerOfGodsStore.setLine2AutoCashOut(line2AutoCashout);
      powerOfGodsStore.setInitialStateReady(true);
    } else {
      notificationStore.setNotification(responseCode);
    }
  }

  async doSpin() {
    powerOfGodsBetsStore.setIsBetsClosed(true);
    if (gameController.store.gameGlobalState === gameGlobalStates.none) {
      gameController.store.setGameGlobalState(gameGlobalStates.waiting);
      const { betAmount } = userInfoStore;
      const doSpinModel = {
        spinData: { betAmount },
      };
      mainGameConnection.doSpin(doSpinModel, true);
    }
  }

  sendCashOut({ action }) {
    powerOfGodsStore.setHasLayerOverSpinButton(true);
    if (!powerOfGodsStore.isAllowCashOut) {
      return;
    }
    const model = {
      action: action,
    };
    powerOfGodsGameConnection.sendCashOut(model);
    soundService.play(PowerOfGodsSoundsNames.OtherButton, true);
  }

  sendPartCashOut({ action }) {
    powerOfGodsStore.setHasLayerOverSpinButton(true);
    if (!powerOfGodsStore.isAllowPartCashOut) {
      return;
    }
    const model = {
      action: action,
    };
    powerOfGodsGameConnection.sendPartCashOut(model);
    soundService.play(PowerOfGodsSoundsNames.OtherButton, true);
  }

  sendBonusFinished() {
    powerOfGodsGameConnection.sendBonusSceneFinished();
    gameController.store.setBonusSceneFinished(true);
  }

  async delay(ms) {
    return new Promise((resolve) => {
      setTimeout(resolve, ms);
    });
  }

  async handleDoSpinResult(payload) {
    const { balance, winAmount, responseCode, freebetInfo, id, key, salt, newHash, hash, result } =
      payload;
    if (responseCode === ValidationType.Success) {
      const {
        possibleActions = [],
        line1,
        line2,
        line3,
        cashoutWinAmount,
        resultSymbol,
        partCashoutWinAmount,
        winInfo,
        bonusInfo,
      } = result;
      powerOfGodsBetsStore.setIsBetsClosed(false);
      if (freebetInfo !== null) {
        freeBetStore.updateCurrentFreeBet(freebetInfo);
      }
      if (!freeBetStore.currentFreeBet) {
        userInfoStore.setUserBalance(balance);
      }

      if (salt && key && newHash && hash) {
        checkHashStore.setLastRoundInfo({ salt, key, hash });
        checkHashStore.setNextRoundHash(newHash);
      }
      checkHashStore.setLastRoundResult({
        id: id,
        resultSymbol: resultSymbol,
      });
      if (winInfo) {
        gameController.store.setHasCashOut(!winInfo.bonusResult);
      } else {
        gameController.store.setHasCashOut(false);
      }
      powerOfGodsStore.setIsAllowBet(possibleActions.includes(actions.bet));
      powerOfGodsStore.setIsAllowCashOut(possibleActions.includes(actions.cashOut));
      powerOfGodsStore.setIsAllowPartCashPut(possibleActions.includes(actions.partCashOut));
      powerOfGodsBetsStore.setIsAllowToChangeBetAmount(!line1 && !line2 && !line3);
      gameController.store.setSpinResult(payload);
      powerOfGodsStore.setWinAmount(winAmount);
      powerOfGodsStore.setCashoutWinAmount(cashoutWinAmount);
      powerOfGodsStore.setPartCashoutWinAmount(partCashoutWinAmount);
      if (bonusInfo) {
        gameController.store.setMustInitBonusScene(bonusInfo);
      }
      if (winInfo) {
        const { winAmount: winAmountOnBonus } = winInfo;
        powerOfGodsStore.setWinAmount(winAmountOnBonus);
      } else {
        const applyWin = () => {
          powerOfGodsStore.setWinAmount(winAmount);
        };

        if (powerOfGodsStore.isWinPopupVisible) {
          const dispose = reaction(
            () => powerOfGodsStore.isWinPopupVisible,
            (visible) => {
              if (!visible) {
                applyWin();
                dispose();
              }
            }
          );
        } else {
          applyWin();
        }
      }
      const symbolToShow =
        result && result.resultSymbol != null ? result.resultSymbol : noWinSymbol;
      gameController.reel.initFromResponse(symbolToShow);

      const hasWinAnimation = Boolean(result && result.winInfo);
      gameController.store.setHasRingWinAnimation(hasWinAnimation);
      checkHashStore.setNextHashTextInfo(TranslationKeys.NextRound);

      const dispose = reaction(
        () => gameController.store.isReelRotating,
        async (isRotating) => {
          if (!isRotating) {
            dispose();
          }
        }
      );
    } else {
      powerOfGodsBetsStore.setIsBetsClosed(false);
      notificationStore.setNotification(responseCode);
      gameController.reel.initFromResponse(noWinSymbol);
      gameController.store.setSpinResult(noWinSymbol);
    }
  }

  handleCashOutResult(payload) {
    const { freebetInfo, responseCode, newHash, result = {} } = payload;
    const {
      possibleActions = [],
      line1,
      line2,
      line3,
      winInfo,
      cashoutWinAmount,
      partCashoutWinAmount,
      winType,
    } = result;
    const { winAmount } = winInfo;
    setTimeout(() => {
      powerOfGodsStore.setHasLayerOverSpinButton(false);
    }, 1000);
    if (responseCode === ValidationType.Success) {
      powerOfGodsStore.setCashoutWinAmount(cashoutWinAmount);
      powerOfGodsStore.setPartCashoutWinAmount(partCashoutWinAmount);
      powerOfGodsBetsStore.setIsAllowToChangeBetAmount(!line1 && !line2 && !line3);
      powerOfGodsStore.setIsAllowBet(possibleActions.includes(actions.bet));
      powerOfGodsStore.setIsAllowCashOut(
        possibleActions.includes(gameController.store.winEnded && actions.cashOut)
      );
      powerOfGodsStore.setIsAllowPartCashPut(
        possibleActions.includes(gameController.store.winEnded && actions.partCashOut)
      );
      if (freebetInfo !== null) {
        freeBetStore.updateCurrentFreeBet(freebetInfo);
      }
      if (newHash) {
        checkHashStore.setNextRoundHash(newHash);
      }
      // checkHashStore.setLastRoundResult({
      //   id: spinId,
      //   resultSymbol: resultSymbol,
      // });
      powerOfGodsStore.setWinAmount(winAmount);
      gameController.doCashOut({ big: line3, middle: line2, small: line1 });
    } else {
      notificationStore.setNotification(responseCode);
    }

    // if (additionalInfo?.hash) {
    //   checkHashStore.setNextRoundHash(additionalInfo.hash);
    // }

    if (winTypesEnum[1] === winType) {
      betHistoryStore.getLastBet(2);
    } else {
      betHistoryStore.getLastBet();
    }
  }

  resetGameData() {
    powerOfGodsStore?.setWinAmount(null);
  }

  resetGameStoreData() {
    powerOfGodsStore.handleToInitStore();
  }

  sendWorker(time) {
    if (time) {
      worker.addEventListener('message', this.countDown);
      worker.postMessage({
        timerValue: time,
        deductionValue: 1,
        intervalDelay: 900,
        precision: 0,
      });
    } else {
      worker.removeEventListener('message', this.countDown);
    }
  }

  //
  // delay(ms) {
  //   return new Promise((resolve) => {
  //     setTimeout(resolve, ms);
  //   });
  // }
}

export const powerOfGodsGameService = new PowerOfGodsGameService();
